import { motion } from 'framer-motion';
import { Calendar, MapPin, PlusCircle, Save, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PrimaryButton from '../../ui/PrimaryButton';
import Input from '../../ui/input.js';
import { stockInApi } from '../Stock_in/stockInApi.js';
import { closingStockApi } from './CloseingStockApi';

const getTodayDate = () => new Date().toISOString().split('T')[0];

const CreateClosingStocks = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const createEmptyRow = () => ({
    stockName: '',
    stock_item_id: null,
    openingStock: '',
    closingStock: '',
    consumption: '',
    unit: '',
  });

  // Initialize from location state if available (same pattern as Stock_out.js)
  // Use location.state directly to ensure we get the data
  const initialState = location.state || {};
  // Check if items exist and are not empty (null means we should fetch, empty array means no items)
  const hasItemsFromState = initialState.items && 
                            Array.isArray(initialState.items) && 
                            initialState.items.length > 0 &&
                            initialState.items !== null;
  const isEditMode = initialState.editMode || false;
  const viewMode = initialState.viewMode || false; // If viewMode is true, start in read-only view
  const editClosingDate = initialState.closing_date || initialState.date;
  
  // Debug logging
  useEffect(() => {
    if (stockRows.length > 0 && (stockRows[0].stock_item_id === '' || stockRows[0].stockName === '')) {
      console.error('CreateClosingStocks: ❌ ERROR - stockRows are empty! Data was not mapped correctly!');
    } else {
    }
  }, []);

  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [stockItems, setStockItems] = useState([]);
  const [loadingStockItems, setLoadingStockItems] = useState(false);
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(false);

  const [date, setDate] = useState(() => editClosingDate || initialState.date || getTodayDate());
  // Set opening stock date from state or use closing date if available
  const [openingStockDate, setOpeningStockDate] = useState(() => {
    if (initialState.openingStockDate) return initialState.openingStockDate;
    // If we have a closing date from state, use it as opening date reference
    if (editClosingDate && hasItemsFromState) return editClosingDate;
    return '';
  });
  const [formSiteId, setFormSiteId] = useState(() => initialState.siteId || '');
  const [stockRows, setStockRows] = useState(() => {
    // Use items from state if available (both edit and non-edit mode)
    if (hasItemsFromState && initialState.items && initialState.items.length > 0) {
      
      // Deduplicate items by stock_item_id - keep the last occurrence (most recent)
      const itemMap = new Map();
      initialState.items.forEach((item, idx) => {
        const stockItemId = item.stock_item_id || item.stockItemId || (item.id && !String(item.id).startsWith('item-') && !String(item.id).startsWith('snapshot-') ? item.id : null);
        if (stockItemId) {
          // If item already exists, replace with newer one (keep last occurrence)
          itemMap.set(stockItemId, item);
        } else {
          // If no stock_item_id, use index as key to preserve order
          itemMap.set(`no-id-${idx}`, item);
        }
      });
      
      // Convert map back to array
      const deduplicatedItems = Array.from(itemMap.values());
      
      return deduplicatedItems.map((item, idx) => {
        // Extract stock_item_id - check if value exists and is valid (not undefined/null/empty)
        let stockItemId = null;
        if (item.stock_item_id && item.stock_item_id !== '' && item.stock_item_id !== 'undefined' && item.stock_item_id !== null) {
          stockItemId = String(item.stock_item_id);
        } else if (item.stockItemId && item.stockItemId !== '' && item.stockItemId !== 'undefined' && item.stockItemId !== null) {
          stockItemId = String(item.stockItemId);
        } else if (item.id && item.id !== '' && item.id !== 'undefined' && item.id !== null && 
                   !String(item.id).startsWith('item-') && !String(item.id).startsWith('snapshot-')) {
          stockItemId = String(item.id);
        }
        
        // Extract stockName - check multiple fields
        let stockName = '';
        if (item.stockName && item.stockName !== '' && item.stockName !== 'undefined') {
          stockName = item.stockName;
        } else if (item.name && item.name !== '' && item.name !== 'undefined') {
          stockName = item.name;
        } else if (item.item_name && item.item_name !== '' && item.item_name !== 'undefined') {
          stockName = item.item_name;
        }
        
        // Handle opening stock - try multiple field names
        // PRIORITIZE openingStock (what was stored when creating), then opening_quantity, then quantity
        // This ensures we use the exact value that was entered when creating, not a derived value
        let opening = 0;
        if (item.openingStock !== undefined && item.openingStock !== null && item.openingStock !== '') {
          // openingStock is the field used in the form and stored in notes - prioritize this
          opening = typeof item.openingStock === 'string' ? parseFloat(item.openingStock) || 0 : item.openingStock;
        } else if (item.opening_quantity !== undefined && item.opening_quantity !== null) {
          opening = typeof item.opening_quantity === 'string' ? parseFloat(item.opening_quantity) || 0 : item.opening_quantity;
        } else if (item.quantity !== undefined && item.quantity !== null) {
          opening = typeof item.quantity === 'string' ? parseFloat(item.quantity) || 0 : item.quantity;
        }
        
        // Handle closing stock - try multiple field names (prioritize numeric values)
        // Check current_quantity, closingQuantity, closing_quantity, then closingStock string
        let closing = 0;
        if (item.current_quantity !== undefined && item.current_quantity !== null) {
          closing = typeof item.current_quantity === 'string' ? parseFloat(item.current_quantity) || 0 : item.current_quantity;
        } else if (item.closingQuantity !== undefined && item.closingQuantity !== null) {
          closing = typeof item.closingQuantity === 'string' ? parseFloat(item.closingQuantity) || 0 : item.closingQuantity;
        } else if (item.closing_quantity !== undefined && item.closing_quantity !== null) {
          closing = typeof item.closing_quantity === 'string' ? parseFloat(item.closing_quantity) || 0 : item.closing_quantity;
        } else if (item.closingStock !== undefined && item.closingStock !== null) {
          closing = typeof item.closingStock === 'string' ? parseFloat(item.closingStock) || 0 : item.closingStock;
        }
        
        // Calculate consumption
        const consumption = Math.max(0, parseFloat(opening || 0) - parseFloat(closing || 0));
        
        const mapped = {
          id: stockItemId || item.id || `item-${idx}`,
          stock_item_id: stockItemId || '', // Only set if we have a valid value
          stockName: stockName,
          openingStock: String(opening),
          closingStock: String(closing),
          consumption: String(consumption),
          unit: item.unit || item.unit_of_measure || '',
        };
        
        return mapped;
      });
    }
    
    return [createEmptyRow()];
  });

  // Re-initialize stockRows if items arrive after component mount (React Router state might be delayed)
  // Use a ref to track if we've already initialized to prevent infinite loops
  const hasInitializedRef = React.useRef(false);
  
  useEffect(() => {
    // Skip if we've already initialized or if we don't have items from state
    if (hasInitializedRef.current || !hasItemsFromState || !initialState.items || initialState.items.length === 0) {
      return;
    }
    
    // Only re-initialize if we don't have proper data yet
    const hasEmptyRows = stockRows.length > 0 && stockRows.every(row => 
      (!row.stock_item_id || row.stock_item_id === '') && 
      (!row.stockName || row.stockName === '')
    );
    
    if (hasEmptyRows) {
      const mappedRows = initialState.items.map((item, idx) => {
        // Extract stock_item_id - check if value exists and is not empty/undefined
        // Handle case where key exists but value is undefined/null/empty string
        let stockItemId = null;
        if (item.stock_item_id && item.stock_item_id !== '' && item.stock_item_id !== 'undefined') {
          stockItemId = item.stock_item_id;
        } else if (item.stockItemId && item.stockItemId !== '' && item.stockItemId !== 'undefined') {
          stockItemId = item.stockItemId;
        } else if (item.id && item.id !== '' && item.id !== 'undefined' && 
                   !String(item.id).startsWith('item-') && 
                   !String(item.id).startsWith('snapshot-')) {
          stockItemId = item.id;
        }
        
        // Extract stockName - check multiple fields
        let stockName = '';
        if (item.stockName && item.stockName !== '') {
          stockName = item.stockName;
        } else if (item.name && item.name !== '') {
          stockName = item.name;
        } else if (item.item_name && item.item_name !== '') {
          stockName = item.item_name;
        }
        
        const opening = item.opening_quantity !== undefined && item.opening_quantity !== null 
          ? item.opening_quantity 
          : (item.quantity !== undefined && item.quantity !== null 
            ? item.quantity 
            : (item.openingStock !== undefined && item.openingStock !== null 
              ? (typeof item.openingStock === 'string' ? parseFloat(item.openingStock) || 0 : item.openingStock)
              : 0));
        const closing = item.current_quantity !== undefined && item.current_quantity !== null 
          ? item.current_quantity 
          : (item.closingQuantity !== undefined && item.closingQuantity !== null 
            ? item.closingQuantity 
            : (item.closingStock !== undefined && item.closingStock !== null 
              ? (typeof item.closingStock === 'string' ? parseFloat(item.closingStock) || 0 : item.closingStock)
              : 0));
        const consumption = Math.max(0, parseFloat(opening || 0) - parseFloat(closing || 0));
        
        return {
          id: item.id || stockItemId || `item-${idx}`,
          stock_item_id: stockItemId ? String(stockItemId) : '', // Only convert to string if not null/undefined
          stockName: stockName,
              openingStock: String(opening),
              closingStock: String(closing),
              consumption: String(consumption),
              unit: item.unit || item.unit_of_measure || '',
            };
      });
      setStockRows(mappedRows);
      hasInitializedRef.current = true; // Mark as initialized to prevent re-running
    }
  }, [hasItemsFromState, initialState.items]); // Removed stockRows from dependencies to prevent infinite loop
  const [errors, setErrors] = useState({
    date: '',
    openingStockDate: '',
    siteId: '',
    rows: [{}],
  });
  const [isLoading, setIsLoading] = useState(false);
  // Start in view mode (read-only) if viewMode is true, otherwise start in edit mode if isEditMode is true
  const [isEditing, setIsEditing] = useState(() => {
    if (viewMode) {
      return false; // Start in view mode (read-only)
    }
    const shouldEdit = isEditMode ? true : false;
    return shouldEdit; // Otherwise, start in edit mode if isEditMode is true
  });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [isMobileView, setIsMobileView] = useState(false);
  const [savedState, setSavedState] = useState(null);
  const [openingStockData, setOpeningStockData] = useState({});
  const [isLoadingOpeningStock, setIsLoadingOpeningStock] = useState(false);
  const [stockInQuantities, setStockInQuantities] = useState({}); // Cache for stock in quantities by item ID
  const [stockInData, setStockInData] = useState({}); // Cache for stock in quantities by item ID


  const hasExistingEntries = stockRows.some((row) => {
    if (!row) {
      return false;
    }
    if (row?.id !== undefined && row?.id !== null) {
      return true;
    }
    if (row?.isExisting || row?.isPersisted || row?.persisted) {
      return true;
    }
    return false;
  });
  // When hasItemsFromState is true (viewing existing data), only allow editing when isEditing is true
  // Otherwise, allow editing when: no existing entries, OR in editing mode, OR in edit mode from view page
  const canEditRows = hasItemsFromState 
    ? isEditing  // If viewing existing data, only editable when explicitly in edit mode
    : (!hasExistingEntries || isEditing || isEditMode); // Otherwise, use normal logic
  
  // Debug: Log edit state
  // Show Edit button only when NOT in edit mode and we have data to view/edit
  const shouldShowEditButton = !isEditing && (isEditMode || hasExistingEntries || hasItemsFromState);
  
  // Show Save button only when in edit mode
  const shouldShowSaveButton = isEditing;

  // Load sites on component mount
  useEffect(() => {
    const loadSites = async () => {
      setLoadingSites(true);
      try {
        const sitesData = await stockInApi.getSites();
        setSites(sitesData || []);
      } catch (err) {
        console.error('Error loading sites:', err);
        setSites([]);
      } finally {
        setLoadingSites(false);
      }
    };

    loadSites();
  }, []);

  // Load stock items on component mount and when site changes
  useEffect(() => {
    const loadStockItems = async () => {
      setLoadingStockItems(true);
      try {
        const params = {};
        if (formSiteId) {
          params.site_id = formSiteId;
        }

        let result = await stockInApi.getStockItems(params);

        if (!result.success && formSiteId && result.error && result.message && result.message.includes('index')) {
          console.warn('Site-filtered query failed (backend index issue), trying without site filter...');
          result = await stockInApi.getStockItems({});
        }

        if (result.success && result.items) {
          const items = result.items.map(item => ({
            id: String(item.stock_item_id || item.id || item._id), // Ensure ID is string for matching
            name: item.stock_name || item.name || item.item_name || 'Unknown Item',
            stock_item_id: String(item.stock_item_id || item.id || item._id), // Ensure stock_item_id is string
            unit: item.unit || item.unit_of_measure || '',
          })).sort((a, b) => a.name.localeCompare(b.name));
          
          setStockItems(items);
        } else {
          console.warn('No stock items found or API error:', result.error || result.message);
          setStockItems([]);
        }
      } catch (err) {
        console.error('Error loading stock items:', err);
        setStockItems([]);
      } finally {
        setLoadingStockItems(false);
      }
    };

    loadStockItems();
  }, [formSiteId]);

  // Sync stock item names when stockItems are loaded and we have rows with stock_item_id
  useEffect(() => {
    if (stockItems.length > 0 && stockRows.length > 0 && hasItemsFromState) {
      setStockRows(prevRows => {
        const updated = prevRows.map(row => {
          // If we have a stock_item_id but no stockName, try to find it in stockItems
          if (row.stock_item_id && (!row.stockName || row.stockName === '')) {
            const matchingItem = stockItems.find(item => 
              String(item.id) === String(row.stock_item_id) || 
              String(item.stock_item_id) === String(row.stock_item_id)
            );
            if (matchingItem) {
              return { ...row, stockName: matchingItem.name, unit: matchingItem.unit || row.unit };
            }
          }
          return row;
        });
        return updated;
      });
    }
  }, [stockItems, hasItemsFromState]);

  // Fetch snapshot items when we have closing_date but no items
  useEffect(() => {
    // Use location.state directly to get latest state
    const currentState = location.state || {};
    const currentItems = currentState.items;
    const currentClosingDate = currentState.closing_date || currentState.date;
    const currentHasItems = currentItems && 
                            Array.isArray(currentItems) && 
                            currentItems.length > 0;
    
    const fetchSnapshotItems = async () => {
      // Only fetch if we have a closing_date, but don't have items yet
      // Also check if items is null (explicitly set to fetch) or empty array
      const itemsIsEmpty = !currentItems || 
                           currentItems === null || 
                           (Array.isArray(currentItems) && currentItems.length === 0);
      
      const shouldFetch = currentClosingDate && !currentHasItems && itemsIsEmpty;
      
      if (!shouldFetch) {
        return;
      }

      setIsLoadingSnapshot(true);
      setError('');
      try {
        const params = {
          limit: 500,
          date: currentClosingDate,
          openingDate: currentClosingDate, // Use camelCase as expected by API
        };

        // Add site filter if available
        const siteIdToFilter = formSiteId || currentState.siteId;

        const result = await closingStockApi.getClosingStock(params);

        if (result.success && result.items && result.items.length > 0) {
          // Filter by site if site_id is provided
          let filteredItems = result.items;
          if (siteIdToFilter) {
            filteredItems = result.items.filter(item => 
              item.site_id === siteIdToFilter || 
              !item.site_id // Include items without site_id
            );
          }

          // Transform API response to match form structure
          const transformedRows = filteredItems.map(item => {
            return {
              id: item.stock_item_id,
              stock_item_id: item.stock_item_id,
              stockName: item.item_name || '',
              openingStock: String(item.opening_quantity || item.current_quantity || ''),
              closingStock: String(item.current_quantity || ''), // current_quantity is closing stock
              consumption: String(
                (parseFloat(item.opening_quantity || 0)) - (parseFloat(item.current_quantity || 0))
              ),
              unit: item.unit_of_measure || '',
            };
          });

          setStockRows(transformedRows);
          
          // Set opening stock date to closing date if not already set
          if (!openingStockDate && currentClosingDate) {
            setOpeningStockDate(currentClosingDate);
          }
          
          // Set site if not already set
          if (!formSiteId && currentState.siteId) {
            setFormSiteId(currentState.siteId);
          }
        } else {
          setError('No items found for this snapshot. You can create a new closing stock entry.');
        }
      } catch (err) {
        console.error('Error fetching snapshot items:', err);
        setError('Failed to load snapshot data. You can still create a new closing stock entry.');
      } finally {
        setIsLoadingSnapshot(false);
      }
    };

    fetchSnapshotItems();
  }, [location.state, editClosingDate, hasItemsFromState, formSiteId, openingStockDate]);

  // Fetch opening stock data when openingStockDate is provided (but don't overwrite existing values)
  useEffect(() => {
    const fetchOpeningStock = async () => {
      if (!openingStockDate) {
        setOpeningStockData({});
        return;
      }

      // If we already have items from state, we don't need to fetch opening stock
      // as the values are already provided
      if (hasItemsFromState) {
        return;
      }

      setIsLoadingOpeningStock(true);
      try {
        const result = await closingStockApi.getClosingStock({
          limit: 500,
          opening_date: openingStockDate,
          date: openingStockDate,
        });

        if (result.success && result.items) {
          // Create a map of stock_item_id to opening stock data
          const stockMap = {};
          result.items.forEach(item => {
            if (item.stock_item_id) {
              stockMap[String(item.stock_item_id)] = {
                opening_quantity: item.opening_quantity || 0,
                opening_value: item.opening_value || 0,
                current_quantity: item.current_quantity || 0,
              };
            }
          });
          setOpeningStockData(stockMap);
        } else {
          console.warn('Failed to fetch opening stock:', result.message);
          setOpeningStockData({});
        }
      } catch (err) {
        console.error('Error fetching opening stock:', err);
        setOpeningStockData({});
      } finally {
        setIsLoadingOpeningStock(false);
      }
    };

    fetchOpeningStock();
  }, [openingStockDate, hasItemsFromState]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');

    const updateView = (event) => {
      setIsMobileView(event.matches);
    };

    updateView(mediaQuery);
    mediaQuery.addEventListener('change', updateView);

    return () => mediaQuery.removeEventListener('change', updateView);
  }, []);

  const handleDateChange = (value) => {
    setDate(value);
    setErrors((prev) => ({ ...prev, date: '' }));
    setSuccess('');
    setError('');
  };

  const handleOpeningStockDateChange = (value) => {
    setOpeningStockDate(value);
    // Clear stock in cache when date changes
    setStockInQuantities({});
    setErrors((prev) => ({ ...prev, openingStockDate: '' }));
    setSuccess('');
    setError('');
  };

  // Function to fetch stock in quantity for a specific item
  const fetchStockInQuantity = async (stockItemId) => {
    if (!stockItemId) {
      return null;
    }

    if (!formSiteId) {
      return null;
    }


    try {
      // Fetch stock in entries for this site, up to the closing date
      const result = await stockInApi.getStockInEntries({
        siteId: formSiteId,
        endDate: date || openingStockDate, // Get entries up to the closing date
        limit: 500,
      });


      if (result.success && result.entries && result.entries.length > 0) {
        // Find entries for this specific stock item
        const itemEntries = result.entries.filter(entry => {
          const entryItemId = entry.stock_item_id || entry.item_id || entry.stockItemId || entry.stock_item_id;
          const matches = String(entryItemId) === String(stockItemId);
          if (matches) {
            console.log('CreateClosingStocks: Found matching entry:', {
              entryItemId,
              stockItemId,
              entry: entry,
            });
          }
          return matches;
        });

        console.log('CreateClosingStocks: Filtered entries for item', stockItemId, ':', itemEntries.length, 'entries');

        if (itemEntries.length > 0) {
          // Sort by date (most recent first) and get the latest entry
          const sortedEntries = itemEntries.sort((a, b) => {
            const dateA = new Date(a.date || a.stock_date || a.event_date || 0);
            const dateB = new Date(b.date || b.stock_date || b.event_date || 0);
            return dateB - dateA; // Most recent first
          });

          const latestEntry = sortedEntries[0];
          console.log('CreateClosingStocks: Latest entry:', latestEntry);
          
          // Try multiple field names for quantity
          const quantity = latestEntry.quantityOrWeight || 
                          latestEntry.quantity || 
                          latestEntry.quantity_or_weight ||
                          latestEntry.quantity_in ||
                          latestEntry.qty ||
                          0;
          
          console.log('CreateClosingStocks: ✅ Found stock in quantity for item', stockItemId, ':', quantity, 'from entry:', {
            date: latestEntry.date || latestEntry.stock_date || latestEntry.event_date,
            quantityOrWeight: latestEntry.quantityOrWeight,
            quantity: latestEntry.quantity,
            quantity_or_weight: latestEntry.quantity_or_weight,
          });
          
          // Update cache
          setStockInQuantities(prev => ({
            ...prev,
            [String(stockItemId)]: quantity,
          }));
          
          return quantity;
        }
      }
      
      // If getStockInEntries didn't work, try getStockInView as fallback
      if (!result.success || !result.entries || result.entries.length === 0) {
        
        try {
          // Extract month from date (YYYY-MM-DD -> YYYY-MM)
          const dateToUse = date || openingStockDate;
          const month = dateToUse ? dateToUse.substring(0, 7) : null;
          
          if (month) {
            const viewResult = await stockInApi.getStockInView({
              site_id: formSiteId,
              month: month,
              limit: 500,
            });
            
            
            if (viewResult.success && viewResult.stock_in_records && viewResult.stock_in_records.length > 0) {
              // Find records for this specific stock item
              const itemRecords = viewResult.stock_in_records.filter(record => {
                const recordItemId = record.stock_item_id || record.item_id || record.stockItemId;
                return String(recordItemId) === String(stockItemId);
              });
              
              if (itemRecords.length > 0) {
                // Sort by date (most recent first)
                const sortedRecords = itemRecords.sort((a, b) => {
                  const dateA = new Date(a.stock_date || a.event_date || a.date || 0);
                  const dateB = new Date(b.stock_date || b.event_date || b.date || 0);
                  return dateB - dateA;
                });
                
                const latestRecord = sortedRecords[0];
                const quantity = latestRecord.quantity || latestRecord.quantityOrWeight || 0;
                
                
                // Update cache
                setStockInQuantities(prev => ({
                  ...prev,
                  [String(stockItemId)]: quantity,
                }));
                
                return quantity;
              }
            }
          }
        } catch (viewErr) {
          console.error('CreateClosingStocks: Error in getStockInView fallback:', viewErr);
        }
      }
      
      console.log('CreateClosingStocks: ⚠️ No stock in entries found for item', stockItemId, {
        success: result.success,
        entriesCount: result.entries?.length || 0,
        message: result.message,
      });
      return 0;
    } catch (err) {
      console.error('CreateClosingStocks: ❌ Error fetching stock in quantity:', err);
      return null;
    }
  };

  const handleSiteChange = (value) => {
    setFormSiteId(value);
    setErrors((prev) => ({ ...prev, siteId: '' }));
    setSuccess('');
    setError('');
  };

  const handleRowChange = (index, field, value) => {
    setStockRows((prevRows) => {
      const nextRows = [...prevRows];
      const updatedRow = { ...nextRows[index], [field]: value };

      // Calculate consumption when opening stock or closing stock changes
      if (field === 'openingStock' || field === 'closingStock') {
        const opening = parseFloat(field === 'openingStock' ? value : updatedRow.openingStock) || 0;
        const closing = parseFloat(field === 'closingStock' ? value : updatedRow.closingStock) || 0;
        const consumption = opening - closing;
        updatedRow.consumption = consumption >= 0 ? consumption.toFixed(2) : '0.00';
      }

      // When stock item is selected, automatically fetch opening stock from stock in
      if (field === 'stock_item_id' && value) {
        const selectedItem = stockItems.find(item => item.id === value);
        if (selectedItem) {
          updatedRow.stockName = selectedItem.name;
          updatedRow.unit = selectedItem.unit;
          
          // Only update opening stock if it's not already set (from state/items)
          // This prevents overwriting values when coming from view page
          if (!hasItemsFromState || !updatedRow.openingStock || updatedRow.openingStock === '0' || updatedRow.openingStock === '') {
            // First check if we have stock in quantity cached
            if (stockInQuantities[String(value)] !== undefined) {
              const stockInQty = stockInQuantities[String(value)];
              updatedRow.openingStock = stockInQty.toString();
              // Recalculate consumption
              const opening = parseFloat(updatedRow.openingStock) || 0;
              const closing = parseFloat(updatedRow.closingStock) || 0;
              updatedRow.consumption = (opening - closing >= 0 ? opening - closing : 0).toFixed(2);
              console.log('CreateClosingStocks: Auto-filled opening stock with stock in quantity', stockInQty);
            } else if (openingStockDate && openingStockData[value]) {
              // Fallback to opening stock from closing stock API if available
              const openingStock = openingStockData[value];
              updatedRow.openingStock = openingStock.opening_quantity.toString();
              // Recalculate consumption
              const opening = parseFloat(updatedRow.openingStock) || 0;
              const closing = parseFloat(updatedRow.closingStock) || 0;
              updatedRow.consumption = (opening - closing >= 0 ? opening - closing : 0).toFixed(2);
            } else {
              // Fetch stock in quantity in the background
              fetchStockInQuantity(value).then((quantity) => {
                if (quantity !== null && quantity !== undefined) {
                  // Update the row with stock in quantity
                  setStockRows((prevRows) => {
                    const updatedRows = [...prevRows];
                    const rowToUpdate = { ...updatedRows[index] };
                    
                    // Only auto-fill if opening stock is still empty
                    if (!rowToUpdate.openingStock || rowToUpdate.openingStock === '0' || rowToUpdate.openingStock === '') {
                      rowToUpdate.openingStock = quantity.toString();
                      // Recalculate consumption
                      const opening = parseFloat(rowToUpdate.openingStock) || 0;
                      const closing = parseFloat(rowToUpdate.closingStock) || 0;
                      rowToUpdate.consumption = (opening - closing >= 0 ? opening - closing : 0).toFixed(2);
                      updatedRows[index] = rowToUpdate;
                      return updatedRows;
                    } else {
                    }
                    return prevRows;
                  });
                } else {
                  console.log('CreateClosingStocks: ⚠️ fetchStockInQuantity returned null/undefined/0, not updating. Value:', quantity);
                }
              }).catch((err) => {
                console.error('CreateClosingStocks: ❌ Error in fetchStockInQuantity promise for item', value, ':', err);
              });
            }
          } else {
            // Recalculate consumption with existing values
            const opening = parseFloat(updatedRow.openingStock) || 0;
            const closing = parseFloat(updatedRow.closingStock) || 0;
            updatedRow.consumption = (opening - closing >= 0 ? opening - closing : 0).toFixed(2);
          }
        }
      }

      nextRows[index] = updatedRow;
      return nextRows;
    });

    setErrors((prev) => ({
      ...prev,
      rows: prev.rows.map((err, idx) =>
        idx === index ? { ...err, [field]: '' } : err
      ),
    }));

    setSuccess('');
    setError('');
  };

  const handleAddRow = () => {
    if (!canEditRows) {
      return;
    }
    setStockRows((prev) => [...prev, createEmptyRow()]);
    setErrors((prev) => ({
      ...prev,
      rows: [...prev.rows, {}],
    }));
  };

  const handleClearRow = (index) => {
    if (!canEditRows) {
      return;
    }
    setStockRows((prev) =>
      prev.map((row, idx) => (idx === index ? createEmptyRow() : row))
    );
    setErrors((prev) => ({
      ...prev,
      rows: prev.rows.map((rowError, idx) =>
        idx === index ? {} : rowError
      ),
    }));
  };

  const handleRemoveRow = (index) => {
    if (!canEditRows) {
      return;
    }
    if (stockRows.length === 1) {
      return;
    }

    setStockRows((prev) => prev.filter((_, idx) => idx !== index));
    setErrors((prev) => ({
      ...prev,
      rows: prev.rows.filter((_, idx) => idx !== index),
    }));
  };

  // Validate form
  const validateForm = () => {
    const rowErrors = stockRows.map((row) => {
      const rowError = {};

      if (!row.stockName.trim()) {
        rowError.stockName = 'Required';
      }

      if (!row.openingStock) {
        rowError.openingStock = 'Required';
      } else if (parseFloat(row.openingStock) < 0) {
        rowError.openingStock = 'Must be greater than or equal to 0';
      }

      if (!row.closingStock && row.closingStock !== '0') {
        rowError.closingStock = 'Required';
      } else if (parseFloat(row.closingStock) < 0) {
        rowError.closingStock = 'Must be greater than or equal to 0';
      } else       if (parseFloat(row.closingStock) > parseFloat(row.openingStock || 0)) {
        rowError.closingStock = 'Cannot exceed opening stock';
      }

      return rowError;
    });

    const dateError = date ? '' : 'Date is required';
    const openingStockDateError = openingStockDate ? '' : 'Opening stock date is required';
    const siteError = formSiteId ? '' : 'Site is required';
    const hasRowErrors = rowErrors.some(
      (rowError) => Object.keys(rowError).length > 0
    );

    setErrors({
      date: dateError,
      openingStockDate: openingStockDateError,
      siteId: siteError,
      rows: rowErrors,
    });

    return !dateError && !openingStockDateError && !siteError && !hasRowErrors;
  };

  const isFormReady =
    !!date &&
    !!openingStockDate &&
    !!formSiteId &&
    stockRows.every((row) => {
      const hasName = row.stockName.trim().length > 0;
      const hasOpeningStock = !!row.openingStock && parseFloat(row.openingStock) >= 0;
      const hasClosingStock = (row.closingStock !== '' && row.closingStock !== null && row.closingStock !== undefined) && parseFloat(row.closingStock) >= 0;
      return hasName && hasOpeningStock && hasClosingStock;
    });

  // Handle form submission
  const handleSubmit = async (e) => {
    // Allow calling without event (for retry)
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (!validateForm()) {
      setError('Please fill in all required fields correctly');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const siteId = formSiteId || (hasItemsFromState ? initialState.siteId : '');
      const siteName = sites.find(s => (s.site_id || s.id) === siteId)?.site_name || 
                      (hasItemsFromState ? initialState.siteName : '');

      // Prepare closing stock payload according to API documentation
      // API expects: closing_date (required), items array (required) with stock_item_id and closing_quantity
      // Store opening stock values in notes field since API doesn't support opening_quantity in items
      const openingStockData = {};
      stockRows.forEach(row => {
        if (row.stock_item_id && row.openingStock) {
          openingStockData[row.stock_item_id] = parseFloat(row.openingStock) || 0;
        }
      });
      
      const closingStockPayload = {
        closing_date: date, // Required: Closing date in YYYY-MM-DD format
        items: stockRows
          .filter(row => row.stock_item_id && row.closingStock !== '' && row.closingStock !== null && parseFloat(row.closingStock) >= 0)
          .map((row) => {
            return {
              stock_item_id: row.stock_item_id, // Required
              closing_quantity: parseFloat(row.closingStock) || 0, // Required, must be >= 0
              // Optional fields - only include if they have values
              ...(row.stockName && { item_name: row.stockName }),
              ...(row.unit && { unit_of_measure: row.unit }),
              ...(row.reorder_threshold !== undefined && row.reorder_threshold !== '' && { reorder_threshold: parseFloat(row.reorder_threshold) || 0 }),
              ...(row.price_per_unit !== undefined && row.price_per_unit !== '' && { price_per_unit: parseFloat(row.price_per_unit) || 0 }),
              ...(row.vendor_name && { vendor_name: row.vendor_name }),
              ...(siteId && { site_id: siteId }),
              ...(siteName && { site_name: siteName }),
            };
          }),
        // Optional top-level fields
        // Store opening stock data in notes field as JSON for retrieval later
        notes: [
          openingStockDate ? `Closing stock snapshot for ${date} (Opening date: ${openingStockDate})` : `Closing stock snapshot for ${date}`,
          Object.keys(openingStockData).length > 0 ? `OPENING_STOCK_DATA:${JSON.stringify(openingStockData)}` : ''
        ].filter(Boolean).join(' | '),
        ...(siteId && { site_id: siteId }),
        ...(siteName && { site_name: siteName }),
      };

      // Validate that we have at least one item
      if (closingStockPayload.items.length === 0) {
        setError('Please add at least one stock item with a closing quantity.');
        return;
      }

      // Call API to create closing stock snapshot
      const result = await closingStockApi.createClosingStock(closingStockPayload);

      if (!result.success) {
        // For 502 errors, provide user-friendly message with retry suggestion
        if (result.status === 502) {
          setError(
            <div className="space-y-3">
              <p className="font-semibold text-red-700">Backend Service Unavailable (502 Error)</p>
              <p className="text-sm">
                The backend service is temporarily unavailable. This could be due to:
              </p>
              <ul className="text-sm list-disc list-inside space-y-1 ml-2">
                <li>Lambda function timeout or error</li>
                <li>Backend service restarting</li>
                <li>Temporary network issue</li>
              </ul>
              <div className="mt-3">
                <button
                  type="button"
                  onClick={async () => {
                    setError('');
                    setSuccess('');
                    // Retry the submission
                    const fakeEvent = { preventDefault: () => {} };
                    await handleSubmit(fakeEvent);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Retry
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/onboarding-close-stock/view')}
                  className="ml-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  View Closing Stock
                </button>
              </div>
              {result.details && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-gray-600">Technical Details</summary>
                  <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto">
                    {JSON.stringify(result.details, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          );
          return;
        }
        
        // For other errors, show standard error message
        const errorMsg = result.details 
          ? `${result.message}\n\nDetails: ${JSON.stringify(result.details, null, 2)}`
          : result.message || result.error || 'Failed to create closing stock snapshot';
        throw new Error(errorMsg);
      }

      // Show simple success message
      setSuccess('Closing stock snapshot created successfully!');
      
      if (hasExistingEntries) {
        setIsEditing(false);
        setSavedState(null);
        setErrors({ date: '', openingStockDate: '', siteId: '', rows: stockRows.map(() => ({})) });
      } else {
        // Reset form after creating new entry
        setDate(getTodayDate());
        setOpeningStockDate('');
        setFormSiteId('');
        setStockRows([createEmptyRow()]);
        setErrors({ date: '', openingStockDate: '', siteId: '', rows: [{}] });
        setIsEditing(false);
        setSavedState(null);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.message || 'Failed to save closing stock. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleEdit = () => {
    if (isLoading) {
      return;
    }

    if (isEditing) {
      // Cancel edit mode - restore original data
      if (savedState) {
        setDate(savedState.date);
        setOpeningStockDate(savedState.openingStockDate || '');
        setFormSiteId(savedState.siteId || '');
        setStockRows(savedState.rows);
        setErrors({
          date: '',
          openingStockDate: '',
          siteId: '',
          rows: savedState.rows.map(() => ({})),
        });
      } else {
        // If no saved state, restore from initial state
        if (hasItemsFromState || editClosingDate) {
          // Restore from initial state
          setDate(editClosingDate || initialState.date || getTodayDate());
          setOpeningStockDate(initialState.openingStockDate || '');
          setFormSiteId(initialState.siteId || '');
          if (hasItemsFromState) {
            setStockRows(initialState.items.map(item => ({
              id: item.id,
              stock_item_id: item.stock_item_id || item.stockItemId || null,
              stockName: item.stockName || item.name || '',
              openingStock: item.quantity || item.openingStock || '',
              closingStock: item.closingQuantity || item.closingStock || '',
              consumption: (item.quantity || item.openingStock || 0) - (item.closingQuantity || item.closingStock || 0),
              unit: item.unit || item.unit_of_measure || '',
            })));
          }
        } else {
          setDate(getTodayDate());
          setOpeningStockDate('');
          setFormSiteId('');
          setStockRows([createEmptyRow()]);
        }
        setErrors({ date: '', openingStockDate: '', siteId: '', rows: [{}] });
      }
      setSuccess('');
      setError('');
      setIsEditing(false);
      setSavedState(null);
      return;
    }

    // Enable edit mode - save current state
    setSavedState({
      date,
      openingStockDate,
      siteId: formSiteId,
      rows: stockRows.map((row) => ({ ...row })),
    });
    setSuccess('');
    setError('');
    setErrors((prev) => ({
      ...prev,
      rows: stockRows.map(() => ({})),
    }));
    setIsEditing(true);
  };

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="w-full h-full min-h-screen">
      <div className="mx-auto h-auto">
        <form onSubmit={handleSubmit} className="h-full space-y-8">

          {/* Loading Snapshot Indicator */}
          {isLoadingSnapshot && (
            <div className="rounded-3xl bg-white p-8 text-center">
              <p className="text-gray-600 text-lg">Loading snapshot data...</p>
            </div>
          )}

          {/* Main Form Section */}
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="rounded-3xl">
              {/* Date Pickers and Site Selection */}
              <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-4">
                <div className="flex-1 flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <label
                      htmlFor="opening-stock-date"
                      className="inline-flex items-center gap-2"
                    >
                      Pick Opening Stock Date
                    </label>
                    <div className="relative mt-1 max-w-xs">
                      <Calendar className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                      <input
                        type="date"
                        id="opening-stock-date"
                        value={openingStockDate}
                        onChange={(e) => handleOpeningStockDateChange(e.target.value)}
                        disabled={!canEditRows || (hasItemsFromState && !isEditing)}
                        readOnly={hasItemsFromState && !isEditing}
                        className={`w-full h-12 rounded-2xl border pl-10 pr-4 text-sm focus:outline-none focus:ring-2 disabled:cursor-not-allowed ${
                          (hasItemsFromState && !isEditing)
                            ? 'bg-gray-100 border-gray-200' 
                            : errors.openingStockDate
                            ? 'bg-white border-red-500 focus:ring-red-500'
                            : 'bg-white border-gray-200 focus:ring-green-500'
                        }`}
                        required
                      />
                    </div>
                    {errors.openingStockDate && (
                      <p className="mt-1 text-xs text-red-600">{errors.openingStockDate}</p>
                    )}
                  </div>
                  <div className="flex-1">
                    <label
                      htmlFor="closing-stock-date"
                      className="inline-flex items-center gap-2"
                    >
                      Pick Closing Stock Date
                    </label>
                    <div className="relative mt-1 max-w-xs">
                      <Calendar className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                      <input
                        type="date"
                        id="closing-stock-date"
                        value={date}
                        onChange={(e) => handleDateChange(e.target.value)}
                        disabled={!canEditRows || (hasItemsFromState && !isEditing)}
                        readOnly={hasItemsFromState && !isEditing}
                        className={`w-full h-12 rounded-2xl border pl-10 pr-4 text-sm focus:outline-none focus:ring-2 disabled:cursor-not-allowed ${
                          (hasItemsFromState && !isEditing)
                            ? 'bg-gray-100 border-gray-200' 
                            : errors.date
                            ? 'bg-white border-red-500 focus:ring-red-500'
                            : 'bg-white border-gray-200 focus:ring-green-500'
                        }`}
                        required
                      />
                    </div>
                    {errors.date && (
                      <p className="mt-1 text-xs text-red-600">{errors.date}</p>
                    )}
                  </div>
                  {!hasItemsFromState && (
                    <div className="flex-1">
                      <label
                        htmlFor="site"
                        className="inline-flex items-center gap-2"
                      >
                        Site
                      </label>
                      <div className="relative mt-1 max-w-xs">
                        <MapPin className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                        <select
                          id="site"
                          value={formSiteId}
                          onChange={(e) => handleSiteChange(e.target.value)}
                          disabled={!canEditRows || loadingSites || (hasItemsFromState && !isEditing)}
                          className={`w-full h-12 rounded-2xl border bg-white pl-10 pr-4 text-sm focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-gray-100 ${
                            errors.siteId
                              ? 'border-red-500 focus:ring-red-500'
                              : 'border-gray-200 focus:ring-green-500'
                          }`}
                          required
                        >
                          <option value="" disabled hidden>
                            {loadingSites ? 'Loading sites...' : 'Select site'}
                          </option>
                          {Array.isArray(sites) && [...sites]
                    .sort((a, b) => {
            const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
            const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((site) => (
                      <option key={site.site_id || site.id} value={site.site_id || site.id}>
                        {site.site_name || site.name || 'Unknown Site'}
                      </option>
                    ))}
                </select>

                      </div>
                      {errors.siteId && (
                        <p className="mt-1 text-xs text-red-600">{errors.siteId}</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-end">
                  {/* Show Create Close Stock button when creating new entry (not viewing/editing existing) */}
                  {!hasItemsFromState && !isEditing && (
                    <PrimaryButton
                      type="submit"
                      disabled={isLoading || !isFormReady || !canEditRows}
                      className="w-full md:w-auto min-w-[200px]"
                    >
                      {isLoading ? (
                        <span className="flex items-center gap-2">
                          <svg
                            className="h-5 w-5 animate-spin text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Creating...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Save className="w-5 h-5" />
                          Create Close Stock
                        </span>
                      )}
                    </PrimaryButton>
                  )}
                  {/* Show Edit button only when NOT in edit mode */}
                  {shouldShowEditButton && (
                    <button
                      type="button"
                      onClick={handleToggleEdit}
                      className="w-full md:w-auto min-w-[120px] h-12 rounded-lg px-6 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 flex items-center justify-center bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-500"
                      style={{ color: '#ffffff' }}
                    >
                      <span
                        style={{
                          color: '#ffffff !important',
                          WebkitTextFillColor: '#ffffff',
                          WebkitTextStroke: 'none',
                        }}
                      >
                        Edit
                      </span>
                    </button>
                  )}
                  {/* Show Cancel and Save buttons only when in edit mode */}
                  {isEditing && (
                    <>
                      <button
                        type="button"
                        onClick={handleToggleEdit}
                        className="w-full md:w-auto min-w-[120px] h-12 rounded-lg px-6 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 flex items-center justify-center bg-red-500 hover:bg-red-600 focus-visible:ring-red-500"
                        style={{ color: '#ffffff' }}
                      >
                        <span
                          style={{
                            color: '#ffffff !important',
                            WebkitTextFillColor: '#ffffff',
                            WebkitTextStroke: 'none',
                          }}
                        >
                          Cancel
                        </span>
                      </button>
                      <PrimaryButton
                        type="submit"
                        disabled={isLoading || !isFormReady || !canEditRows}
                        className="w-full md:w-auto min-w-[200px]"
                      >
                      {isLoading ? (
                        <span className="flex items-center gap-2">
                          <svg
                            className="h-5 w-5 animate-spin text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Saving...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Save className="w-5 h-5" />
                          Close Stock
                        </span>
                      )}
                    </PrimaryButton>
                    </>
                  )}
                </div>
              </div>

              {/* Stock Table */}
              <div className="relative group">
                {!isMobileView && (
                  <>
                    <div className="overflow-x-auto rounded-3xl border border-gray-200 bg-white shadow-sm">
                      <table className="min-w-full border-collapse text-sm md:text-base">
                        <thead>
                          <tr className="bg-gray-50 text-left text-gray-600">
                            <th className="px-4 py-3 font-semibold">S.No</th>
                            <th className="px-4 py-3 font-semibold min-w-[160px]">
                              Items
                            </th>
                            <th className="px-4 py-3 font-semibold">
                              Opening Stock
                            </th>
                            <th className="px-4 py-3 font-semibold">Closing Stock</th>
                            <th className="px-4 py-3 font-semibold">Consumption</th>
                            <th className="px-4 py-3 font-semibold text-right"> </th>
                          </tr>
                        </thead>
                        <tbody>
                          {stockRows.length === 0 ? (
                            <tr>
                              <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                {hasItemsFromState ? (
                                  <div>
                                    <p className="text-red-600 font-semibold">⚠️ Items received but not mapped correctly</p>
                                    <p className="text-sm mt-2">Check console for details</p>
                                    <p className="text-xs mt-1">Items count: {initialState.items?.length || 0}</p>
                                  </div>
                                ) : (
                                  'No items found. Please add items to continue.'
                                )}
                              </td>
                            </tr>
                          ) : (
                            stockRows.map((row, index) => (
                            <tr
                              key={`stock-row-${index}`}
                              className="border-t border-gray-100"
                            >
                              <td className="px-4 py-4 align-top text-gray-800">
                                {index + 1}
                              </td>
                              <td className="px-1 py-1 min-w-[160px]">
                                <div className="relative">
                                  {!canEditRows && row.stockName ? (
                                    // Show item name as text in view mode
                                    <div className="h-11 w-full rounded-2xl border border-gray-200 bg-gray-100 px-4 py-2.5 text-sm text-gray-700">
                                      {row.stockName}
                                    </div>
                                  ) : (
                                    <select
                                      value={row.stock_item_id || ''}
                                      onChange={(e) => {
                                        const selectedItemId = e.target.value;
                                        const selectedItem = stockItems.find(item => item.id === selectedItemId);
                                        if (selectedItem) {
                                          handleRowChange(index, 'stock_item_id', selectedItemId);
                                          handleRowChange(index, 'stockName', selectedItem.name);
                                          handleRowChange(index, 'unit', selectedItem.unit);
                                        } else {
                                          handleRowChange(index, 'stock_item_id', '');
                                          handleRowChange(index, 'stockName', '');
                                          handleRowChange(index, 'unit', '');
                                        }
                                      }}
                                      disabled={!canEditRows || loadingStockItems}
                                      className={`h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm shadow-none focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                                        errors.rows[index]?.stockName
                                          ? 'border-red-500 focus:ring-red-500'
                                          : 'border-gray-200 focus:ring-green-500'
                                      }`}
                                    >
                                      <option value="">
                                        {loadingStockItems ? 'Loading items...' : 'Select item'}
                                      </option>
                                      {stockItems.map((item) => (
                                        <option key={item.id} value={item.id}>
                                          {item.name}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </div>
                                {errors.rows[index]?.stockName && (
                                  <p className="mt-1 text-xs text-red-600">
                                    {errors.rows[index].stockName}
                                  </p>
                                )}
                              </td>
                              <td className="px-1 py-1">
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={row.openingStock || ''}
                                  onChange={(e) =>
                                    handleRowChange(
                                      index,
                                      'openingStock',
                                      e.target.value
                                    )
                                  }
                                  placeholder="0.00"
                                  disabled={!canEditRows}
                                  className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none ${
                                    errors.rows[index]?.openingStock
                                      ? 'border-red-500 focus:ring-red-500'
                                      : 'border-gray-200 focus:ring-green-500'
                                  }`}
                                />
                                {errors.rows[index]?.openingStock && (
                                  <p className="mt-1 text-xs text-red-600">
                                    {errors.rows[index].openingStock}
                                  </p>
                                )}
                              </td>
                              <td className="px-1 py-1">
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={row.closingStock || ''}
                                  onChange={(e) =>
                                    handleRowChange(index, 'closingStock', e.target.value)
                                  }
                                  placeholder="0.00"
                                  disabled={!canEditRows}
                                  className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none ${
                                    errors.rows[index]?.closingStock
                                      ? 'border-red-500 focus:ring-red-500'
                                      : 'border-gray-200 focus:ring-green-500'
                                  }`}
                                />
                                {errors.rows[index]?.closingStock && (
                                  <p className="mt-1 text-xs text-red-600">
                                    {errors.rows[index].closingStock}
                                  </p>
                                )}
                              </td>
                              <td className="px-1 py-1">
                                <Input
                                  type="number"
                                  value={row.consumption || '0'}
                                  readOnly
                                  placeholder="0.00"
                                  className="h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-600 shadow-none"
                                />
                              </td>
                              <td className="px-1 py-1 text-right">
                                <button
                                  type="button"
                                  onClick={() =>
                                    index === 0
                                      ? handleClearRow(index)
                                      : handleRemoveRow(index)
                                  }
                                  disabled={!canEditRows || (index !== 0 && stockRows.length === 1)}
                                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                    index === 0
                                      ? 'border-gray-200 text-gray-600 hover:border-gray-300'
                                      : stockRows.length === 1
                                        ? 'cursor-not-allowed border-gray-200 text-gray-300'
                                        : 'border-red-100 text-red-600 hover:border-red-300 hover:text-red-700'
                                  }`}
                                  aria-label={index === 0 ? 'Clear row values' : 'Remove item row'}
                                >
                                  <XCircle className="h-4 w-4" />
                                  {index === 0 ? 'Clear' : 'Close'}
                                </button>
                              </td>
                            </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="pointer-events-none mt-4 flex justify-center">
                      <button
                        type="button"
                        onClick={handleAddRow}
                        disabled={!canEditRows}
                        className="pointer-events-auto inline-flex translate-y-2 items-center gap-2 rounded-full border border-dashed border-gray-400 bg-white px-5 py-2 text-sm font-semibold text-gray-700 shadow-sm opacity-0 transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100 focus-visible:translate-y-0 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <PlusCircle className="h-4 w-4" />
                        Add Item
                      </button>
                    </div>
                  </>
                )}

                {isMobileView && (
                  <div className="space-y-4 rounded-3xl border border-gray-200 bg-white p-4 shadow-sm">
                    {stockRows.map((row, index) => (
                      <div
                        key={`stock-card-${index}`}
                        className="rounded-2xl border border-gray-100 p-4"
                      >
                        <div className="mb-3 flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-700">
                            Item #{index + 1}
                          </p>
                          <button
                            type="button"
                            onClick={() =>
                              index === 0 ? handleClearRow(index) : handleRemoveRow(index)
                            }
                            disabled={!canEditRows || (index !== 0 && stockRows.length === 1)}
                            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                              index === 0
                                ? 'border-gray-200 text-gray-600 hover:border-gray-300'
                                : stockRows.length === 1
                                  ? 'cursor-not-allowed border-gray-200 text-gray-300'
                                  : 'border-red-100 text-red-600 hover:border-red-300 hover:text-red-700'
                            }`}
                          >
                            <XCircle className="h-4 w-4" />
                            {index === 0 ? 'Clear' : 'Close'}
                          </button>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Items
                            </label>
                            <div className="relative">
                              {!canEditRows && row.stockName ? (
                                // Show item name as text in view mode
                                <div className="h-11 w-full rounded-2xl border border-gray-200 bg-gray-100 px-4 py-2.5 text-sm text-gray-700">
                                  {row.stockName}
                                </div>
                              ) : (
                                <select
                                  value={row.stock_item_id || ''}
                                  onChange={(e) => {
                                    const selectedItemId = e.target.value;
                                    const selectedItem = stockItems.find(item => item.id === selectedItemId);
                                    if (selectedItem) {
                                      handleRowChange(index, 'stock_item_id', selectedItemId);
                                      handleRowChange(index, 'stockName', selectedItem.name);
                                      handleRowChange(index, 'unit', selectedItem.unit);
                                    } else {
                                      handleRowChange(index, 'stock_item_id', '');
                                      handleRowChange(index, 'stockName', '');
                                      handleRowChange(index, 'unit', '');
                                    }
                                  }}
                                  disabled={!canEditRows || loadingStockItems}
                                  className={`h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm shadow-none focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                                    errors.rows[index]?.stockName
                                      ? 'border-red-500 focus:ring-red-500'
                                      : 'border-gray-200 focus:ring-green-500'
                                  }`}
                                >
                                  <option value="">
                                    {loadingStockItems ? 'Loading items...' : 'Select item'}
                                  </option>
                                  {stockItems.map((item) => (
                                    <option key={item.id} value={item.id}>
                                      {item.name}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                            {errors.rows[index]?.stockName && (
                              <p className="mt-1 text-xs text-red-600">
                                {errors.rows[index].stockName}
                              </p>
                            )}
                          </div>

                          <div className="grid grid-cols-1 gap-3">
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-500">
                                Opening Stock
                              </label>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={row.openingStock}
                                onChange={(e) =>
                                  handleRowChange(index, 'openingStock', e.target.value)
                                }
                                placeholder="0.00"
                                disabled={!canEditRows}
                                className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none ${
                                  errors.rows[index]?.openingStock
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-200 focus:ring-green-500'
                                }`}
                              />
                              {errors.rows[index]?.openingStock && (
                                <p className="mt-1 text-xs text-red-600">
                                  {errors.rows[index].openingStock}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-500">
                                Closing Stock
                              </label>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={row.closingStock}
                                onChange={(e) =>
                                  handleRowChange(index, 'closingStock', e.target.value)
                                }
                                placeholder="0.00"
                                disabled={!canEditRows}
                                className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none ${
                                  errors.rows[index]?.closingStock
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-200 focus:ring-green-500'
                                }`}
                              />
                              {errors.rows[index]?.closingStock && (
                                <p className="mt-1 text-xs text-red-600">
                                  {errors.rows[index].closingStock}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-500">
                                Consumption
                              </label>
                              <Input
                                type="number"
                                value={row.consumption}
                                readOnly
                                placeholder="0.00"
                                className="h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-600 shadow-none"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={handleAddRow}
                        disabled={!canEditRows}
                        className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-400 bg-white px-5 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <PlusCircle className="h-4 w-4" />
                        Add Item
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>


          {/* Success/Error Messages */}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-lg bg-green-100 text-green-800"
            >
              {typeof success === 'string' ? (
                <p className="text-green-600">{success}</p>
              ) : (
                success
              )}
            </motion.div>
          )}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-lg bg-red-100 text-red-800"
            >
              {typeof error === 'string' ? error : error}
            </motion.div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CreateClosingStocks;

